/**
 * Standalone panel rendering the full 11-key upload-attribution Record.
 *
 * Two callers:
 *   - ExecutionLedger detail pane (when the operator selects a
 *     `kind:"upload"` row)
 *   - RuntimeFsBrowser viewer pane (when the operator clicks a file
 *     whose path matches an upload-ledger row; the panel sits above
 *     the file content)
 *
 * Layout mirrors handoff §3.1. All 4 writer classes handled via
 * `resolveUploaderDisplay`. Click-to-copy on the id + sha256.
 *
 * Re-uploaded-since-stamp warning (§3.2) is NOT implemented — no
 * head-only sha lookup against the runtime fs proxy today.
 */

import { Copy } from "lucide-react";

import { SourceChip } from "@/components/uploads/SourceChip";
import { formatAbsUtc, formatRelative } from "@/components/uploads/upload-time";
import {
  resolveUploaderDisplay,
  type ExecutionLedgerRecord,
} from "@/types/execution-ledger";

interface Props {
  record: ExecutionLedgerRecord;
  /** Optional compact variant — slightly smaller padding + smaller font;
   *  used in the FsBrowser viewer pane where the panel sits above file
   *  content and shouldn't dominate. */
  dense?: boolean;
}

export function UploadAttributionPanel({ record, dense }: Props) {
  const uploader = resolveUploaderDisplay(
    record.uploadedById,
    record.uploadedByEmail,
  );
  return (
    <div
      className={`rounded-md border border-amber-200 bg-amber-50/40 ${dense ? "p-2" : "p-3"} dark:border-amber-900/50 dark:bg-amber-950/20`}
      data-testid="upload-attribution-panel"
    >
      <h3 className={`mb-2 ${dense ? "text-xs" : "text-sm"} font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-300`}>
        Upload attribution
      </h3>
      <dl className={`grid grid-cols-[max-content_1fr] gap-x-4 gap-y-${dense ? "1" : "2"} text-sm`}>
        <Dt>Uploaded by</Dt>
        <Dd>
          <div className="flex flex-col gap-1">
            <span className={uploader.isUnknown ? "text-slate-400" : ""}>
              {uploader.label}
            </span>
            {record.uploadedByRoleSnapshot && (
              <span className="inline-flex w-fit rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] uppercase text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                {record.uploadedByRoleSnapshot}
              </span>
            )}
            {record.uploadedById && !uploader.isSystem && !uploader.isUnknown && (
              <CopyableMono value={record.uploadedById} />
            )}
          </div>
        </Dd>

        <Dt>Uploaded at</Dt>
        <Dd>
          <div className="flex flex-col gap-0.5">
            <span title={formatAbsUtc(record.uploadedAt)}>
              {formatAbsUtc(record.uploadedAt)}
            </span>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {formatRelative(record.uploadedAt)}
            </span>
          </div>
        </Dd>

        <Dt>Source</Dt>
        <Dd>
          <SourceChip source={record.uploadSource} />
        </Dd>

        {record.originalFilename && (
          <>
            <Dt>Filename</Dt>
            <Dd className="break-all font-mono text-xs">
              {record.originalFilename}
            </Dd>
          </>
        )}

        {record.originalSize !== undefined && (
          <>
            <Dt>Size</Dt>
            <Dd className="text-xs">{fmtBytes(record.originalSize)}</Dd>
          </>
        )}

        {record.originalContentType && (
          <>
            <Dt>Content type</Dt>
            <Dd className="font-mono text-xs">{record.originalContentType}</Dd>
          </>
        )}

        {record.originalSha256 && (
          <>
            <Dt>SHA-256</Dt>
            <Dd>
              <CopyableMono
                value={record.originalSha256}
                display={`${record.originalSha256.slice(0, 12)}…${record.originalSha256.slice(-4)}`}
              />
            </Dd>
          </>
        )}

        {record.ingestModality && (
          <>
            <Dt>Modality</Dt>
            <Dd className="font-mono text-xs">{record.ingestModality}</Dd>
          </>
        )}

        {record.blobPath && (
          <>
            <Dt>Blob path</Dt>
            <Dd className="break-all font-mono text-xs">{record.blobPath}</Dd>
          </>
        )}
      </dl>
    </div>
  );
}

function Dt({ children }: { children: React.ReactNode }) {
  return (
    <dt className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
      {children}
    </dt>
  );
}

function Dd({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <dd className={`text-sm ${className}`}>{children}</dd>;
}

function CopyableMono({
  value,
  display,
}: {
  value: string;
  display?: string;
}) {
  return (
    <button
      type="button"
      title={`${value} (click to copy)`}
      onClick={() => {
        void navigator.clipboard?.writeText(value).catch(() => undefined);
      }}
      className="inline-flex max-w-full items-baseline gap-1 break-all font-mono text-xs text-slate-700 hover:text-indigo-600 dark:text-slate-300 dark:hover:text-indigo-400"
    >
      {display ?? value}
      <Copy className="h-2.5 w-2.5 opacity-50" />
    </button>
  );
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} kB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
